import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  COMMAND_CENTER_SECTIONS,
  DRILL_METRICS,
  REPORT_PERIODS,
  type CommandCenter,
  type CommandCenterSection,
  type DrillMetric,
  type DrillResult,
  type ReportPeriod,
  type ResolvedReportFilters,
} from '@nest-recruit/shared';
import { apiGet, type ApiError } from '../../../lib/api-client';

export const COMMAND_CENTER_KEY = ['command-center'] as const;
export const DRILL_KEY = ['report-drill'] as const;

/** F17.03's filters, kept in the address so a view can be shared and the back button works. */
export interface ReportUrl {
  readonly period?: ReportPeriod;
  readonly from?: string;
  readonly to?: string;
  readonly ventureId?: string;
  readonly departmentId?: string;
  readonly recruiterUserId?: string;
}

export interface CommandCenterUrl extends ReportUrl {
  /** N-70 and N-71 open a section directly (Q18). */
  readonly section?: CommandCenterSection;
  /** S31 (Q21): money also shown in the reporting currency, at each amount's own date's rate. */
  readonly convert?: true;
}

export interface DrillUrl extends ReportUrl {
  readonly metric?: DrillMetric;
  readonly key?: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readFilters(search: Readonly<Record<string, unknown>>): ReportUrl {
  const text = (key: string, pattern: RegExp): string | undefined => {
    const value = search[key];
    return typeof value === 'string' && pattern.test(value) ? value : undefined;
  };
  const period = REPORT_PERIODS.find((value) => value === search.period);
  return {
    ...(period ? { period } : {}),
    ...(text('from', ISO_DATE) ? { from: text('from', ISO_DATE) } : {}),
    ...(text('to', ISO_DATE) ? { to: text('to', ISO_DATE) } : {}),
    ...(text('ventureId', UUID) ? { ventureId: text('ventureId', UUID) } : {}),
    ...(text('departmentId', UUID) ? { departmentId: text('departmentId', UUID) } : {}),
    ...(text('recruiterUserId', UUID) ? { recruiterUserId: text('recruiterUserId', UUID) } : {}),
  };
}

export function readCommandCenterUrl(search: Readonly<Record<string, unknown>>): CommandCenterUrl {
  const section = COMMAND_CENTER_SECTIONS.find((value) => value === search.section);
  return { ...readFilters(search), ...(section ? { section } : {}), ...(search.convert === true ? { convert: true } : {}) };
}

export function readDrillUrl(search: Readonly<Record<string, unknown>>): DrillUrl {
  const metric = DRILL_METRICS.find((value) => value === search.metric);
  const key = typeof search.key === 'string' ? search.key.slice(0, 120) : undefined;
  return { ...readFilters(search), ...(metric ? { metric } : {}), ...(key ? { key } : {}) };
}

/** The filters as the server applied them, back into the address form. */
export function urlOf(filters: ResolvedReportFilters): ReportUrl {
  return {
    period: filters.period,
    ...(filters.period === 'custom' ? { from: filters.from, to: filters.to } : {}),
    ...(filters.ventureId ? { ventureId: filters.ventureId } : {}),
    ...(filters.departmentId ? { departmentId: filters.departmentId } : {}),
    ...(filters.recruiterUserId ? { recruiterUserId: filters.recruiterUserId } : {}),
  };
}

/** The path of one figure's records. */
export function drillHref(filters: ResolvedReportFilters, metric: DrillMetric, key = ''): string {
  const params = new URLSearchParams({ metric, ...(key ? { key } : {}) });
  for (const [name, value] of Object.entries(urlOf(filters))) if (typeof value === 'string') params.set(name, value);
  return `/reports/drill?${params.toString()}`;
}

function queryOf(url: ReportUrl): Record<string, string | undefined> {
  return { period: url.period, from: url.from, to: url.to, ventureId: url.ventureId, departmentId: url.departmentId, recruiterUserId: url.recruiterUserId };
}

/** A filter change swaps the figures in place; nothing drops to a skeleton (§5a rule 2). */
export function useCommandCenter(url: CommandCenterUrl, isEnabled: boolean): UseQueryResult<CommandCenter, ApiError> {
  const query = { ...queryOf(url), convert: url.convert ? '1' : undefined };
  return useQuery({
    queryKey: [...COMMAND_CENTER_KEY, query],
    queryFn: () => apiGet<CommandCenter>('/reports/command-center', { query }),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
    refetchInterval: 60_000,
  });
}

export function useDrill(url: DrillUrl, isEnabled: boolean): UseQueryResult<DrillResult, ApiError> {
  return useQuery({
    queryKey: [...DRILL_KEY, url],
    queryFn: () => apiGet<DrillResult>('/reports/drill', { query: { ...queryOf(url), metric: url.metric, key: url.key } }),
    enabled: isEnabled && url.metric !== undefined,
    placeholderData: (previous) => previous,
  });
}
